import { FC } from 'react';
import Alert from '@mui/joy/Alert';
import { IconButton } from '@mui/joy';
import CloseIcon from '@mui/icons-material/Close';
import './Toaster.scss';

export type ToasterItem = {
  id: string;
  message: string;
  color?: AlertColor;
  variant?: AlertVariant;
  direction?: Direction;
  size?: ToastSize;
};

type ToasterProps = {
  toasts: ToasterItem[];
  onClose: (id: string) => void;
};

const groupByDirection = (
  toasts: ToasterItem[]
): Record<Direction, ToasterItem[]> => {
  const groups = {} as Record<Direction, ToasterItem[]>;
  toasts.forEach((toast) => {
    const direction = toast.direction || 'top-right';
    (groups[direction] ||= []).push(toast);
  });
  return groups;
};

const ToastCard: FC<{ toast: ToasterItem; onClose: (id: string) => void }> = ({
  toast,
  onClose,
}) => (
  <Alert
    className="toast-card"
    variant={toast.variant || 'soft'}
    color={toast.color || 'primary'}
    size={toast.size || 'md'}
    endDecorator={
      <IconButton
        size="sm"
        variant="plain"
        color={toast.color === 'danger' ? 'danger' : 'neutral'}
        onClick={() => onClose(toast.id)}
      >
        <CloseIcon />
      </IconButton>
    }
  >
    <div className="toast-content-wrapper">
      {toast.message.split('\n').map((line, index) => (
        <div className="toast-line" key={index}>
          {line}
        </div>
      ))}
    </div>
  </Alert>
);

/**
 * Renders active toasts as a stack. Each direction gets its own fixed,
 * anchored column so bursts of toasts queue up and remain readable instead of
 * replacing one another.
 */
export const Toaster: FC<ToasterProps> = ({ toasts, onClose }) => {
  const groups = groupByDirection(toasts);

  return (
    <>
      {Object.entries(groups).map(([direction, items]) => (
        <div key={direction} className={`toast-stack toast-stack--${direction}`}>
          {items.map((toast) => (
            <ToastCard key={toast.id} toast={toast} onClose={onClose} />
          ))}
        </div>
      ))}
    </>
  );
};
